/* ========== CHART-TYPE DETECTOR ==========
 * Scans a <c:plotArea> for chart-type elements (barChart, lineChart,
 * pieChart, etc.) and reads each one's per-type attributes:
 *
 *   grouping     — standard / stacked / percentStacked (bar/line/area)
 *   barDir       — bar / col (barChart only)
 *   ofPieType    — pie / bar (ofPieChart only)
 *   hasMarker    — chart-level marker val=1 OR per-series marker symbol
 *   is3D         — derived from element name (bar3DChart, line3DChart, …)
 *   ofPieGeom    — gapWidth / secondPieSize / split rules (ofPieChart only)
 *
 * Each chart-type-specific detection is a small function below; the main
 * detectChartGroups() composes them.
 * ========================================== */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { STD_CHART_TYPES, RENDER_TYPE_MAP, IS_3D_TYPES } from "./constants";

/** <c:grouping val="..."/> as direct child, default "standard". */
function readGrouping(el) {
  const node = qDirectChild(el, "grouping") || qLocal(el, "grouping");
  return node ? (node.getAttribute("val") || "standard") : "standard";
}

/** <c:barDir val="bar|col"/> — only on barChart/bar3DChart.
 *  Per ECMA-376 the default when the element/attr is absent is "col". */
function readBarDir(el) {
  const node = qDirectChild(el, "barDir") || qLocal(el, "barDir");
  return node ? (node.getAttribute("val") || "col") : "col";
}

/** <c:ofPieType val="pie|bar"/> — only on ofPieChart. */
function readOfPieType(el) {
  const node = qDirectChild(el, "ofPieType") || qLocal(el, "ofPieType");
  return node ? (node.getAttribute("val") || "pie") : null;
}

/** <c:radarStyle val="standard|marker|filled"/> — only on radarChart. */
function readRadarStyle(el) {
  const node = qDirectChild(el, "radarStyle") || qLocal(el, "radarStyle");
  return node ? (node.getAttribute("val") || "standard") : null;
}

/**
 * Excel writes markers in two places — we accept either:
 *   1. Chart-level <c:marker val="1"/> (set when "Line with Markers" was picked)
 *   2. Per-series <c:ser><c:marker><c:symbol val="circle|square|..."/>
 */
function readHasMarker(el) {
  // Chart-level direct child
  const directChildren = Array.from(el.childNodes).filter(n =>
    n.nodeType === 1 && (
      n.localName === "marker" ||
      (n.tagName && n.tagName.split(":").pop() === "marker")
    )
  );
  for (const m of directChildren) {
    if (m.getAttribute("val") === "1") return true;
  }
  // Per-series marker with a non-"none" symbol
  const serNodes = qLocalAll(el, "ser");
  for (const ser of serNodes) {
    const serMarker = qDirectChild(ser, "marker");
    if (!serMarker) continue;
    const symbolNode = qDirectChild(serMarker, "symbol") || qLocal(serMarker, "symbol");
    const symbolVal = symbolNode ? symbolNode.getAttribute("val") : null;
    if (symbolVal && symbolVal !== "none") return true;
  }
  return false;
}

/**
 * Read ofPieChart-only geometry. Excel defaults when attrs are absent:
 *   gapWidth=100, secondPieSize=75, splitType="auto", splitPos=2
 *
 * Note: the OOXML schema says splitType default is "auto" and splitPos
 * default is 0, but every modern Excel-authored file with no explicit
 * splitPos visually shows the last 2 values in the secondary plot, so we
 * use splitPos=2 as the practical default.
 */
function readOfPieGeom(el) {
  const gapWidthNode = qDirectChild(el, "gapWidth");
  const secondPieSizeNode = qDirectChild(el, "secondPieSize");
  const splitTypeNode = qDirectChild(el, "splitType");
  const splitPosNode = qDirectChild(el, "splitPos");
  return {
    gapWidth:      gapWidthNode ? parseInt(gapWidthNode.getAttribute("val") || "100", 10) : 100,
    secondPieSize: secondPieSizeNode ? parseInt(secondPieSizeNode.getAttribute("val") || "75", 10) : 75,
    splitType:     splitTypeNode ? (splitTypeNode.getAttribute("val") || "auto") : "auto",
    splitPos:      splitPosNode ? parseFloat(splitPosNode.getAttribute("val") || "2") : 2,
  };
}

/**
 * Read the group-level <c:dLbls> data-label switches. Excel writes
 * <c:showVal val="1"/>, <c:showPercent val="1"/>, <c:showCatName val="1"/>
 * etc. when the user enables data labels. When no dLbls element exists
 * (or <c:delete val="1"/> is set) NO labels should be drawn — rendering
 * percentages on a chart that never asked for them is a bug.
 */
function readDataLabels(el) {
  const dLbls = qDirectChild(el, "dLbls");
  if (!dLbls) return null;
  const readFlag = (name) => {
    const n = qDirectChild(dLbls, name);
    return !!n && n.getAttribute("val") === "1";
  };
  const delNode = qDirectChild(dLbls, "delete");
  if (delNode && delNode.getAttribute("val") === "1") return null;
  const flags = {
    showVal:     readFlag("showVal"),
    showPercent: readFlag("showPercent"),
    showCatName: readFlag("showCatName"),
    showSerName: readFlag("showSerName"),
  };
  if (!flags.showVal && !flags.showPercent && !flags.showCatName && !flags.showSerName) return null;
  return flags;
}

/**
 * Scan a plotArea for every recognized chart-type element and return one
 * descriptor per group. Each descriptor carries enough info for the
 * renderer to pick the right drawer.
 */
export function detectChartGroups(plotArea) {
  const groups = [];
  for (const typeName of STD_CHART_TYPES) {
    const found = qLocalAll(plotArea, typeName);
    found.forEach(el => {
      const renderType = RENDER_TYPE_MAP[typeName] || typeName.replace("3DChart", "").replace("Chart", "");
      groups.push({
        element: el,
        typeName,
        renderType,
        grouping:   readGrouping(el),
        barDir:     readBarDir(el),
        ofPieType:  readOfPieType(el),
        hasMarker:  readHasMarker(el),
        is3D:       !!IS_3D_TYPES[typeName],
        dataLabels: readDataLabels(el),
        ofPieGeom:  typeName === "ofPieChart" ? readOfPieGeom(el) : null,
        radarStyle: typeName === "radarChart" ? readRadarStyle(el) : null,
      });
    });
  }
  return groups;
}
